import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Autocomplete, FormControlLabel, Switch, Tabs, Tab,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Description as PrescriptionIcon, Person as PersonIcon,
  Cancel as CancelIcon, ContentCopy as DuplicateIcon, MoreVert as MoreIcon,
  MedicalServices as MedicalIcon, Visibility as ViewIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
  LocalPharmacy as PharmacyIcon, Warning as WarningIcon,
  Favorite as HeartIcon, Schedule as ScheduleIcon,
  AutoAwesome as AiIcon, LibraryBooks as TemplateIcon, Save as SaveIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import SendDocumentDialog from '../../components/SendDocumentDialog';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { CLINIC_DOC_SIGNATURE_CSS } from '../../utils/clinicPrintLetterhead';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const typeLabels: Record<string, string> = {
  simple: 'Simples', controlled_blue: 'Controlado (Azul)', controlled_yellow: 'Controlado (Amarelo)',
  special_white: 'Especial (Branco)', antimicrobial: 'Antimicrobiano', dental: 'Odontológico',
};
const typeColors: Record<string, string> = {
  simple: '#14b8a6', controlled_blue: '#3b82f6', controlled_yellow: '#f59e0b',
  special_white: '#8b5cf6', antimicrobial: '#ef4444', dental: '#06b6d4',
};
const statusLabels: Record<string, string> = { active: 'Ativa', cancelled: 'Cancelada', expired: 'Expirada' };
const statusColors: Record<string, string> = { active: '#10b981', cancelled: '#ef4444', expired: '#f59e0b' };

const routeOptions = ['Oral', 'Sublingual', 'Tópica', 'Intramuscular', 'Intravenosa', 'Subcutânea', 'Retal', 'Nasal', 'Inalatória', 'Oftálmica', 'Otológica', 'Vaginal'];
const formOptions = ['Comprimido', 'Cápsula', 'Drágea', 'Solução Oral', 'Suspensão', 'Xarope', 'Gotas', 'Pomada', 'Creme', 'Gel', 'Solução Injetável', 'Supositório', 'Aerossol', 'Spray', 'Colírio', 'Sachê', 'Pó'];

const emptyItem = {
  medication_name: '', active_ingredient: '', concentration: '', pharmaceutical_form: '',
  dosage: '', frequency: '', route: 'Oral', duration: '', quantity: '',
  instructions: '', special_instructions: '', is_controlled: false, control_class: '',
  is_antimicrobial: false, is_continuous_use: false, sort_order: 0,
};

const formatDate = (d: string) => { if (!d) return '-'; const dt = new Date(d + 'T12:00:00'); return dt.toLocaleDateString('pt-BR'); };

const Prescriptions: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [stats, setStats] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<any>(null);
  const [viewPrescription, setViewPrescription] = useState<any>(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateEditingId, setTemplateEditingId] = useState<string | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', description: '', specialty: '', category: '',
    prescription_type: 'simple', default_diagnosis: '', default_diagnosis_code: '', default_notes: '',
  });
  const [templateItems, setTemplateItems] = useState<any[]>([{ ...emptyItem }]);

  // AI prescription states
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPatientName, setAiPatientName] = useState('');
  const [aiMedicationName, setAiMedicationName] = useState('');
  const [aiError, setAiError] = useState('');

  // Form states
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [professionalSearch, setProfessionalSearch] = useState('');
  const [professionalOptions, setProfessionalOptions] = useState<any[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  const [items, setItems] = useState<any[]>([{ ...emptyItem }]);
  const [prescriptionForm, setPrescriptionForm] = useState({
    prescription_type: 'simple',
    diagnosis: '', diagnosis_code: '', clinical_indication: '',
    notes: '', internal_notes: '', is_continuous_use: false, renewal_date: '',
  });
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  const fetchPrescriptions = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.prescription_type = typeFilter;
      const res = await axios.get(getApiUrl('/clinic/prescriptions'), { params });
      setPrescriptions(res.data?.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/prescriptions/stats'));
      setStats(res.data?.data);
    } catch (err) { console.error(err); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/prescriptions/templates'), { params: { is_active: true } });
      setTemplates(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchPrescriptions(); fetchStats(); fetchTemplates(); }, [search, statusFilter, typeFilter]);

  // Search patients
  useEffect(() => {
    if (!dialogOpen) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions-search/patients'), { params: { search: patientSearch || '' } });
        setPatientOptions(res.data?.data || []);
      } catch (err) { console.error(err); }
    }, patientSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [patientSearch, dialogOpen]);

  useEffect(() => { if (dialogOpen) setPatientSearch(''); }, [dialogOpen]);

  // Search professionals
  useEffect(() => {
    if (!dialogOpen) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: professionalSearch || '' } });
        setProfessionalOptions(res.data?.data || []);
      } catch (err) { console.error(err); }
    }, professionalSearch.length > 0 ? 300 : 100);
    return () => clearTimeout(timer);
  }, [professionalSearch, dialogOpen]);

  const resetForm = () => {
    setSelectedPatient(null); setSelectedProfessional(null);
    setPatientSearch(''); setPatientOptions([]); setProfessionalSearch(''); setProfessionalOptions([]);
    setPrescriptionForm({ prescription_type: 'simple', diagnosis: '', diagnosis_code: '', clinical_indication: '', notes: '', internal_notes: '', is_continuous_use: false, renewal_date: '' });
    setItems([{ ...emptyItem }]); setEditingId(null); setTabValue(0);
  };

  const resetTemplateForm = () => {
    setTemplateEditingId(null);
    setTemplateForm({
      name: '', description: '', specialty: '', category: '',
      prescription_type: prescriptionForm.prescription_type || 'simple',
      default_diagnosis: prescriptionForm.diagnosis || '',
      default_diagnosis_code: prescriptionForm.diagnosis_code || '',
      default_notes: prescriptionForm.notes || '',
    });
    setTemplateItems(items.length > 0 ? items.map((i: any) => ({ ...i })) : [{ ...emptyItem }]);
  };

  const addItem = () => setItems([...items, { ...emptyItem, sort_order: items.length }]);
  const removeItem = (idx: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx: number, field: string, value: any) => {
    const updated = [...items]; updated[idx] = { ...updated[idx], [field]: value }; setItems(updated);
  };

  const handleSave = async () => {
    if (!selectedPatient) { setSnackbar({ open: true, message: 'Selecione um paciente', severity: 'error' }); return; }
    if (!items.some(i => i.medication_name.trim())) { setSnackbar({ open: true, message: 'Adicione pelo menos um medicamento', severity: 'error' }); return; }

    setSaving(true);
    try {
      const payload = {
        ...prescriptionForm,
        patient_id: selectedPatient.id,
        professional_id: selectedProfessional?.id || null,
        items: items.filter(i => i.medication_name.trim()).map((item, idx) => ({ ...item, sort_order: idx })),
      };

      if (editingId) {
        await axios.put(getApiUrl(`/clinic/prescriptions/${editingId}`), payload);
        setSnackbar({ open: true, message: 'Receituário atualizado com sucesso!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/prescriptions'), payload);
        setSnackbar({ open: true, message: 'Receituário criado com sucesso!', severity: 'success' });
      }
      setDialogOpen(false); resetForm(); fetchPrescriptions(); fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar receituário', severity: 'error' });
    }
    setSaving(false);
  };

  const applyTemplate = async (template: any) => {
    setPrescriptionForm((prev: any) => ({
      ...prev,
      prescription_type: template.prescription_type || prev.prescription_type,
      diagnosis: template.default_diagnosis || '',
      diagnosis_code: template.default_diagnosis_code || '',
      notes: template.default_notes || '',
    }));
    const tplItems = (template.items || []).map((it: any, idx: number) => ({
      ...emptyItem,
      ...it,
      id: undefined,
      template_id: undefined,
      created_at: undefined,
      updated_at: undefined,
      sort_order: idx,
    }));
    setItems(tplItems.length > 0 ? tplItems : [{ ...emptyItem }]);
    setSnackbar({ open: true, message: `Template aplicado: ${template.name}`, severity: 'success' });
  };

  const openTemplateCreate = () => {
    resetTemplateForm();
    setTemplateDialogOpen(true);
  };

  const openTemplateManager = async () => {
    await fetchTemplates();
    setTemplateManagerOpen(true);
  };

  const openTemplateEdit = (template: any) => {
    setTemplateEditingId(template.id);
    setTemplateForm({
      name: template.name || '',
      description: template.description || '',
      specialty: template.specialty || '',
      category: template.category || '',
      prescription_type: template.prescription_type || 'simple',
      default_diagnosis: template.default_diagnosis || '',
      default_diagnosis_code: template.default_diagnosis_code || '',
      default_notes: template.default_notes || '',
    });
    const tplItems = (template.items || []).length > 0
      ? (template.items || []).map((it: any, idx: number) => ({ ...emptyItem, ...it, sort_order: idx }))
      : [{ ...emptyItem }];
    setTemplateItems(tplItems);
    setTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) {
      setSnackbar({ open: true, message: 'Informe o nome do template', severity: 'error' });
      return;
    }
    if (!templateItems.some(i => i.medication_name?.trim())) {
      setSnackbar({ open: true, message: 'Adicione ao menos um medicamento no template', severity: 'error' });
      return;
    }

    setTemplateSaving(true);
    try {
      const payload = {
        ...templateForm,
        items: templateItems
          .filter(i => i.medication_name?.trim())
          .map((item, idx) => {
            const { id, template_id, created_at, updated_at, ...rest } = item;
            return { ...rest, sort_order: idx };
          }),
      };
      if (templateEditingId) {
        await axios.put(getApiUrl(`/clinic/prescriptions/templates/${templateEditingId}`), payload);
      } else {
        await axios.post(getApiUrl('/clinic/prescriptions/templates'), payload);
      }
      setTemplateDialogOpen(false);
      await fetchTemplates();
      setSnackbar({ open: true, message: 'Template salvo com sucesso', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar template', severity: 'error' });
    } finally {
      setTemplateSaving(false);
    }
  };

  const deleteTemplate = async (id: string) => {
    if (!window.confirm('Excluir este template pré-preenchido?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/prescriptions/templates/${id}`));
      await fetchTemplates();
      setSnackbar({ open: true, message: 'Template excluído', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir template', severity: 'error' });
    }
  };

  const handleEdit = async (prescription: any) => {
    setEditingId(prescription.id);
    setSelectedPatient({ id: prescription.patient_id, full_name: prescription.patient_name, cpf: prescription.patient_cpf, phone: prescription.patient_phone });
    if (prescription.professional_id) {
      setSelectedProfessional({ id: prescription.professional_id, full_name: prescription.professional_name, registration_number: prescription.professional_registration, specialty: prescription.professional_specialty });
    }
    setPrescriptionForm({
      prescription_type: prescription.prescription_type, diagnosis: prescription.diagnosis || '',
      diagnosis_code: prescription.diagnosis_code || '', clinical_indication: prescription.clinical_indication || '',
      notes: prescription.notes || '', internal_notes: prescription.internal_notes || '',
      is_continuous_use: prescription.is_continuous_use || false, renewal_date: prescription.renewal_date || '',
    });
    setItems((prescription.items || []).length > 0 ? prescription.items : [{ ...emptyItem }]);
    setTabValue(0); setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este receituário?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/prescriptions/${id}`));
      setSnackbar({ open: true, message: 'Receituário excluído', severity: 'success' });
      fetchPrescriptions(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const handleCancel = async (id: string) => {
    const reason = window.prompt('Motivo do cancelamento:');
    if (reason === null) return;
    try {
      await axios.post(getApiUrl(`/clinic/prescriptions/${id}/cancel`), { reason });
      setSnackbar({ open: true, message: 'Receituário cancelado', severity: 'success' });
      fetchPrescriptions(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao cancelar', severity: 'error' }); }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await axios.post(getApiUrl(`/clinic/prescriptions/${id}/duplicate`));
      setSnackbar({ open: true, message: 'Receituário duplicado com sucesso!', severity: 'success' });
      fetchPrescriptions(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao duplicar', severity: 'error' }); }
  };

  const handlePrint = async (prescription: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/prescriptions/${prescription.id}/print`));
      setPrintData(res.data?.data); setPrintDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao gerar impressão', severity: 'error' }); }
  };

  const handleView = async (prescription: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/prescriptions/${prescription.id}`));
      setViewPrescription(res.data?.data); setViewDialogOpen(true);
    } catch (err) { console.error(err); }
  };

  const executePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Receituário</title><style>
      @page { size: A4; margin: 15mm; }
      body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 0; }
      .print-container { max-width: 700px; margin: 0 auto; padding: 20px; }
      .doc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 3px solid #14b8a6; }
      .doc-head-logo { flex-shrink: 0; }
      .doc-head-logo img { display: block; max-width: 150px; max-height: 56px; width: auto; height: auto; object-fit: contain; object-position: left top; }
      .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
      .doc-head-meta { flex-shrink: 0; max-width: 42%; font-size: 10px; color: #6b7280; text-align: right; line-height: 1.45; }
      .doc-head .clinic-name { font-size: 18px; font-weight: 700; color: #0d9488; margin: 0; }
      .clinic-subtitle { font-size: 11px; color: #6b7280; margin-top: 2px; }
      .prescription-title { text-align: center; font-size: 16px; font-weight: 700; color: #0d9488; margin: 15px 0 5px; text-transform: uppercase; letter-spacing: 1px; }
      .prescription-type-badge { text-align: center; margin-bottom: 15px; }
      .prescription-type-badge span { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; }
      .prescription-number { text-align: center; font-size: 10px; color: #9ca3af; margin-bottom: 15px; }
      .section { margin-bottom: 12px; }
      .section-title { font-weight: 700; font-size: 12px; color: #0d9488; margin-bottom: 6px; padding-bottom: 3px; border-bottom: 1px solid #d1fae5; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
      .info-item { font-size: 11px; line-height: 1.4; }
      .info-label { font-weight: 600; color: #374151; }
      .info-value { color: #4b5563; }
      .medication-item { margin-bottom: 12px; padding: 10px; border: 1px solid #e5e7eb; border-radius: 6px; page-break-inside: avoid; }
      .medication-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
      .medication-name { font-weight: 700; font-size: 13px; color: #111; }
      .medication-number { font-size: 10px; color: #9ca3af; font-weight: 600; }
      .medication-details { font-size: 11px; color: #4b5563; line-height: 1.6; }
      .medication-details strong { color: #374151; }
      .medication-instructions { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #e5e7eb; font-size: 10px; color: #6b7280; }
      .controlled-badge { display: inline-block; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; color: #fff; background: #ef4444; margin-left: 8px; }
      ${CLINIC_DOC_SIGNATURE_CSS}
      .validity { text-align: left; margin-top: 24px; margin-bottom: 0; font-size: 10px; color: #6b7280; }
      .footer-note { text-align: center; margin-top: 8px; font-size: 9px; color: #9ca3af; }
      .via-info { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px dashed #d1d5db; font-size: 9px; color: #9ca3af; font-style: italic; }
    </style></head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  const handleAiGenerate = async () => {
    if (!aiPatientName.trim() || !aiMedicationName.trim()) {
      setAiError('Preencha o nome do paciente e do medicamento');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/prescriptions/ai-generate'), {
        patient_name: aiPatientName.trim(),
        medication_name: aiMedicationName.trim(),
      });

      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');

      const rawText = typeof aiData === 'string' ? aiData : (aiData.output || aiData.text || aiData.response || aiData.prescription || JSON.stringify(aiData));

      resetForm();

      const patientMatch = patientOptions.find((p: any) =>
        p.full_name?.toLowerCase().includes(aiPatientName.toLowerCase())
      );
      if (patientMatch) setSelectedPatient(patientMatch);

      const parsed = parseAiPrescription(rawText, aiMedicationName.trim());
      setItems(parsed.items.length > 0 ? parsed.items : [{ ...emptyItem, medication_name: aiMedicationName.trim() }]);
      if (parsed.diagnosis) setPrescriptionForm(prev => ({ ...prev, diagnosis: parsed.diagnosis, clinical_indication: parsed.clinicalIndication || '' }));
      if (parsed.notes) setPrescriptionForm(prev => ({ ...prev, notes: parsed.notes }));

      setAiDialogOpen(false);
      setDialogOpen(true);
      setTabValue(1);
      setSnackbar({ open: true, message: 'Receituário gerado com IA! Revise e ajuste antes de salvar.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar prescrição com IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const parseAiPrescription = (text: string, fallbackMedication: string) => {
    const result: { items: any[]; diagnosis: string; clinicalIndication: string; notes: string } = {
      items: [], diagnosis: '', clinicalIndication: '', notes: '',
    };

    const lines = text.split('\n').map((l: string) => l.trim()).filter((l: string) => l);

    let currentItem: any = null;

    for (const line of lines) {
      const lower = line.toLowerCase();

      if (lower.startsWith('diagnóstico:') || lower.startsWith('diagnostico:')) {
        result.diagnosis = line.split(':').slice(1).join(':').trim();
        continue;
      }
      if (lower.startsWith('indicação clínica:') || lower.startsWith('indicacao clinica:')) {
        result.clinicalIndication = line.split(':').slice(1).join(':').trim();
        continue;
      }
      if (lower.startsWith('observaç') || lower.startsWith('observac') || lower.startsWith('nota:') || lower.startsWith('notas:')) {
        result.notes = line.split(':').slice(1).join(':').trim();
        continue;
      }

      if (lower.startsWith('medicamento:') || lower.startsWith('nome:') || /^\d+[\.\)\-]/.test(line)) {
        if (currentItem) result.items.push(currentItem);
        const medName = line.replace(/^\d+[\.\)\-]\s*/, '').replace(/^(medicamento|nome):\s*/i, '').trim();
        currentItem = { ...emptyItem, medication_name: medName };
        continue;
      }

      if (currentItem) {
        if (lower.startsWith('posologia:') || lower.startsWith('dosagem:') || lower.startsWith('dose:')) {
          currentItem.dosage = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('frequência:') || lower.startsWith('frequencia:')) {
          currentItem.frequency = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('via:') || lower.startsWith('via de administração:')) {
          currentItem.route = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('duração:') || lower.startsWith('duracao:')) {
          currentItem.duration = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('quantidade:')) {
          currentItem.quantity = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('concentração:') || lower.startsWith('concentracao:')) {
          currentItem.concentration = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('forma farmacêutica:') || lower.startsWith('forma:')) {
          currentItem.pharmaceutical_form = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('princípio ativo:') || lower.startsWith('principio ativo:')) {
          currentItem.active_ingredient = line.split(':').slice(1).join(':').trim();
        } else if (lower.startsWith('instrução:') || lower.startsWith('instruções:') || lower.startsWith('instrucoes:') || lower.startsWith('instrucao:')) {
          currentItem.instructions = line.split(':').slice(1).join(':').trim();
        }
      }
    }

    if (currentItem) result.items.push(currentItem);

    if (result.items.length === 0) {
      result.items = [{ ...emptyItem, medication_name: fallbackMedication, instructions: text }];
    }

    return result;
  };

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#111', fontSize: { xs: '1.5rem', md: '2rem' } }}>Receituários</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerencie receituários simples e controlados para seus pacientes</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => { fetchPrescriptions(); fetchStats(); }} sx={{ color: '#14b8a6' }}><RefreshIcon /></IconButton>
          <Button variant="outlined" startIcon={<AiIcon />}
            onClick={() => { setAiPatientName(''); setAiMedicationName(''); setAiError(''); setAiDialogOpen(true); }}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(139,92,246,0.04)' }, textTransform: 'none', fontWeight: 600 }}>
            Criar com IA
          </Button>
          <Button variant="outlined" startIcon={<TemplateIcon />} onClick={openTemplateManager}
            sx={{ borderColor: '#0ea5e9', color: '#0284c7', '&:hover': { borderColor: '#0284c7', bgcolor: 'rgba(14,165,233,0.04)' }, textTransform: 'none', fontWeight: 600 }}>
            Pré-preenchidos
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setDialogOpen(true); }}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            Novo Receituário
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Receituários Ativos', value: stats.total_active, icon: <PrescriptionIcon />, color: '#14b8a6' },
            { label: 'Emitidos Hoje', value: stats.total_today, icon: <ScheduleIcon />, color: '#3b82f6' },
            { label: 'Este Mês', value: stats.total_month, icon: <PharmacyIcon />, color: '#8b5cf6' },
            { label: 'Controlados', value: stats.total_controlled, icon: <WarningIcon />, color: '#f59e0b' },
            { label: 'Uso Contínuo', value: stats.total_continuous_use, icon: <HeartIcon />, color: '#ef4444' },
          ].map((kpi, idx) => (
            <Grid item xs={6} sm={4} md key={idx}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${kpi.color}15` }}>
                    {React.cloneElement(kpi.icon, { sx: { color: kpi.color, fontSize: 24 } })}
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#111' }}>{kpi.value}</Typography>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>{kpi.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField fullWidth size="small" placeholder="Buscar por paciente, profissional ou número..." value={search}
                onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={typeFilter} label="Tipo" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box>
        ) : prescriptions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PrescriptionIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6b7280' }}>Nenhum receituário encontrado</Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mb: 2 }}>Comece criando um novo receituário</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setDialogOpen(true); }}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>
              Novo Receituário
            </Button>
            <Button variant="outlined" startIcon={<TemplateIcon />} onClick={openTemplateManager}
              sx={{ borderColor: '#0ea5e9', color: '#0284c7', textTransform: 'none', mt: 1 }}>
              Pré-preenchidos
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Itens</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prescriptions.map((p: any) => (
                  <TableRow key={p.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdf4' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#0d9488' }}>{p.prescription_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.patient_name}</Typography>
                      {p.patient_cpf && <Typography variant="caption" sx={{ color: '#9ca3af' }}>{p.patient_cpf}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.professional_name || '-'}</Typography>
                      {p.professional_registration && <Typography variant="caption" sx={{ color: '#9ca3af' }}>{p.professional_registration}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={typeLabels[p.prescription_type] || p.prescription_type} size="small"
                        sx={{ bgcolor: `${typeColors[p.prescription_type]}20`, color: typeColors[p.prescription_type], fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell>{(p.items || []).length} med.</TableCell>
                    <TableCell>{formatDate(p.issue_date)}</TableCell>
                    <TableCell>
                      <Chip label={statusLabels[p.status] || p.status} size="small"
                        sx={{ bgcolor: `${statusColors[p.status]}20`, color: statusColors[p.status], fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Visualizar"><IconButton size="small" onClick={() => handleView(p)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Imprimir"><IconButton size="small" onClick={() => handlePrint(p)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Enviar"><IconButton size="small" onClick={() => { setSendDocData({ id: p.id, patientId: p.patient_id, email: p.patient_email, phone: p.patient_phone, name: p.patient_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Mais opções">
                        <IconButton size="small" onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedPrescription(p); }}><MoreIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* More options menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { if (selectedPrescription) handleEdit(selectedPrescription); setAnchorEl(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedPrescription) handleDuplicate(selectedPrescription.id); setAnchorEl(null); }}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon><ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedPrescription) handlePrint(selectedPrescription); setAnchorEl(null); }}>
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon><ListItemText>Imprimir</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedPrescription) { setSendDocData({ id: selectedPrescription.id, patientId: selectedPrescription.patient_id, email: selectedPrescription.patient_email, phone: selectedPrescription.patient_phone, name: selectedPrescription.patient_name }); setSendDialogOpen(true); } setAnchorEl(null); }}><ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon><ListItemText>Enviar para paciente</ListItemText></MenuItem>
        {selectedPrescription?.status === 'active' && (
          <MenuItem onClick={() => { if (selectedPrescription) handleCancel(selectedPrescription.id); setAnchorEl(null); }}>
            <ListItemIcon><CancelIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon><ListItemText>Cancelar</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { if (selectedPrescription) handleDelete(selectedPrescription.id); setAnchorEl(null); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon><ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      {/* ====== CREATE/EDIT DIALOG ====== */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrescriptionIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{editingId ? 'Editar Receituário' : 'Novo Receituário'}</Typography>
          </Box>
          <IconButton onClick={() => { setDialogOpen(false); resetForm(); }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {templates.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0284c7' }}>
                  Receituários Pré-preenchidos (acesso rápido)
                </Typography>
                <Button size="small" startIcon={<SaveIcon />} onClick={openTemplateCreate}
                  sx={{ color: '#0284c7', textTransform: 'none', fontWeight: 600 }}>
                  Salvar atual como pré-preenchido
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {templates.slice(0, 8).map((tpl: any) => (
                  <Chip
                    key={tpl.id}
                    label={tpl.name}
                    onClick={() => applyTemplate(tpl)}
                    clickable
                    sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                  />
                ))}
                <Chip
                  label="Gerenciar templates"
                  onClick={openTemplateManager}
                  clickable
                  variant="outlined"
                  sx={{ borderColor: '#0284c7', color: '#0284c7', fontWeight: 600 }}
                />
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1, borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' },
            '& .Mui-selected': { color: '#14b8a6 !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#14b8a6' },
          }}>
            <Tab label="Paciente & Profissional" />
            <Tab label="Medicamentos" />
            <Tab label="Informações Clínicas" />
          </Tabs>

          {/* TAB 0: PATIENT & PROFESSIONAL */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Tipo de Receituário</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={prescriptionForm.prescription_type} label="Tipo"
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, prescription_type: e.target.value })}>
                {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Paciente *</Typography>
            <Autocomplete
              options={patientOptions}
              getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` - CPF: ${o.cpf}` : ''}${o.phone ? ` - Tel: ${o.phone}` : ''}`}
              value={selectedPatient}
              onChange={(_, v) => setSelectedPatient(v)}
              onInputChange={(_, v) => setPatientSearch(v)}
              onOpen={() => { if (patientOptions.length === 0) setPatientSearch(''); }}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label="Buscar paciente" placeholder="Digite nome, CPF ou telefone..." fullWidth />}
              noOptionsText="Nenhum paciente encontrado"
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0d9488' }}>Profissional Prescritor</Typography>
            <Autocomplete
              options={professionalOptions}
              getOptionLabel={(o: any) => `${o.full_name}${o.registration_number ? ` - ${o.registration_type || 'CRM'} ${o.registration_number}` : ''}${o.specialty ? ` (${o.specialty})` : ''}`}
              value={selectedProfessional}
              onChange={(_, v) => setSelectedProfessional(v)}
              onInputChange={(_, v) => setProfessionalSearch(v)}
              onOpen={() => { if (professionalOptions.length === 0) setProfessionalSearch(''); }}
              filterOptions={(x) => x}
              renderInput={(params) => <TextField {...params} label="Buscar profissional" placeholder="Digite nome ou registro..." fullWidth />}
              noOptionsText="Nenhum profissional encontrado"
            />
          </TabPanel>

          {/* TAB 1: MEDICATIONS */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d9488' }}>Medicamentos</Typography>
              <Button size="small" startIcon={<AddItemIcon />} onClick={addItem}
                sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Adicionar Medicamento</Button>
            </Box>

            {items.map((item, index) => (
              <Card key={index} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e5e7eb', position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Medicamento {index + 1}</Typography>
                  {items.length > 1 && (
                    <IconButton size="small" onClick={() => removeItem(index)} sx={{ color: '#ef4444' }}><RemoveIcon /></IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label="Nome do Medicamento *" value={item.medication_name}
                      onChange={(e) => updateItem(index, 'medication_name', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Princípio Ativo" value={item.active_ingredient}
                      onChange={(e) => updateItem(index, 'active_ingredient', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Concentração" value={item.concentration}
                      onChange={(e) => updateItem(index, 'concentration', e.target.value)} placeholder="Ex: 500mg" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Forma Farmacêutica</InputLabel>
                      <Select value={item.pharmaceutical_form} label="Forma Farmacêutica"
                        onChange={(e) => updateItem(index, 'pharmaceutical_form', e.target.value)}>
                        {formOptions.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Posologia (Dosagem) *" value={item.dosage}
                      onChange={(e) => updateItem(index, 'dosage', e.target.value)} placeholder="Ex: 1 comprimido" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Frequência *" value={item.frequency}
                      onChange={(e) => updateItem(index, 'frequency', e.target.value)} placeholder="Ex: 8 em 8 horas" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Via de Administração</InputLabel>
                      <Select value={item.route} label="Via de Administração"
                        onChange={(e) => updateItem(index, 'route', e.target.value)}>
                        {routeOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth size="small" label="Duração" value={item.duration}
                      onChange={(e) => updateItem(index, 'duration', e.target.value)} placeholder="Ex: 7 dias" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth size="small" label="Quantidade" value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)} placeholder="Ex: 1 caixa" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <FormControlLabel control={<Switch size="small" checked={item.is_controlled} onChange={(e) => updateItem(index, 'is_controlled', e.target.checked)} color="error" />}
                        label={<Typography variant="caption">Controlado</Typography>} />
                      <FormControlLabel control={<Switch size="small" checked={item.is_antimicrobial} onChange={(e) => updateItem(index, 'is_antimicrobial', e.target.checked)} color="warning" />}
                        label={<Typography variant="caption">Antimicrobiano</Typography>} />
                      <FormControlLabel control={<Switch size="small" checked={item.is_continuous_use} onChange={(e) => updateItem(index, 'is_continuous_use', e.target.checked)} color="info" />}
                        label={<Typography variant="caption">Uso Contínuo</Typography>} />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Instruções de Uso" value={item.instructions}
                      onChange={(e) => updateItem(index, 'instructions', e.target.value)}
                      placeholder="Ex: Tomar após as refeições, não ingerir com leite" multiline rows={2} />
                  </Grid>
                </Grid>
              </Card>
            ))}
          </TabPanel>

          {/* TAB 2: CLINICAL INFO */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Diagnóstico" value={prescriptionForm.diagnosis}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, diagnosis: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="CID-10" value={prescriptionForm.diagnosis_code}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, diagnosis_code: e.target.value })} placeholder="Ex: J06.9" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Indicação Clínica" value={prescriptionForm.clinical_indication}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, clinical_indication: e.target.value })} multiline rows={2} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={prescriptionForm.is_continuous_use} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, is_continuous_use: e.target.checked })} color="primary" />}
                  label="Receituário de Uso Contínuo" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Observações (visíveis no receituário impresso)" value={prescriptionForm.notes}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, notes: e.target.value })} multiline rows={3} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notas Internas (NÃO aparecem no receituário impresso)" value={prescriptionForm.internal_notes}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, internal_notes: e.target.value })} multiline rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fef3c7' } }} />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => { setDialogOpen(false); resetForm(); }} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600, minWidth: 160 }}>
            {saving ? <CircularProgress size={20} /> : editingId ? 'Salvar Alterações' : 'Criar Receituário'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====== VIEW DIALOG ====== */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrescriptionIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Receituário {viewPrescription?.prescription_number}</Typography>
            {viewPrescription && (
              <>
                <Chip label={typeLabels[viewPrescription.prescription_type]} size="small" sx={{ bgcolor: `${typeColors[viewPrescription.prescription_type]}20`, color: typeColors[viewPrescription.prescription_type], fontWeight: 600 }} />
                <Chip label={statusLabels[viewPrescription.status]} size="small" sx={{ bgcolor: `${statusColors[viewPrescription.status]}20`, color: statusColors[viewPrescription.status], fontWeight: 600 }} />
              </>
            )}
          </Box>
          <IconButton onClick={() => setViewDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {viewPrescription && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Paciente</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewPrescription.patient_name}</Typography>
                  {viewPrescription.patient_cpf && <Typography variant="body2"><strong>CPF:</strong> {viewPrescription.patient_cpf}</Typography>}
                  {viewPrescription.patient_phone && <Typography variant="body2"><strong>Telefone:</strong> {viewPrescription.patient_phone}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Profissional</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewPrescription.professional_name || '-'}</Typography>
                  {viewPrescription.professional_registration && <Typography variant="body2"><strong>Registro:</strong> {viewPrescription.professional_registration}</Typography>}
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Medicamentos Prescritos</Typography>
              {(viewPrescription.items || []).map((item: any, idx: number) => (
                <Card key={item.id || idx} sx={{ mb: 1.5, p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {idx + 1}. {item.medication_name}
                      {item.concentration && ` ${item.concentration}`}
                      {item.pharmaceutical_form && ` - ${item.pharmaceutical_form}`}
                    </Typography>
                    {item.is_controlled && <Chip label="Controlado" size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600 }} />}
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4b5563' }}>
                    <strong>Posologia:</strong> {item.dosage}, {item.frequency}
                    {item.route && ` (via ${item.route})`}
                    {item.duration && ` por ${item.duration}`}
                  </Typography>
                  {item.quantity && <Typography variant="body2" sx={{ color: '#4b5563' }}><strong>Quantidade:</strong> {item.quantity}</Typography>}
                  {item.instructions && <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, fontStyle: 'italic' }}>{item.instructions}</Typography>}
                </Card>
              ))}

              {viewPrescription.diagnosis && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Informações Clínicas</Typography>
                  <Typography variant="body2"><strong>Diagnóstico:</strong> {viewPrescription.diagnosis}{viewPrescription.diagnosis_code ? ` (CID: ${viewPrescription.diagnosis_code})` : ''}</Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => { if (viewPrescription) handlePrint(viewPrescription); }} startIcon={<PrintIcon />}
            sx={{ color: '#14b8a6', textTransform: 'none', fontWeight: 600 }}>Imprimir</Button>
          <Button onClick={() => { if (viewPrescription) handleEdit(viewPrescription); setViewDialogOpen(false); }} startIcon={<EditIcon />}
            sx={{ color: '#3b82f6', textTransform: 'none', fontWeight: 600 }}>Editar</Button>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ textTransform: 'none' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ====== PRINT DIALOG ====== */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Pré-visualização do Receituário</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={executePrint}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>Imprimir</Button>
            <IconButton onClick={() => setPrintDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {printData && (
            <div ref={printRef}>
              <div className="print-container">
                {/* Header */}
                <div className="doc-head">
                  {printData.clinic?.logo_url && (
                    <div className="doc-head-logo"><img src={printData.clinic.logo_url} alt="" /></div>
                  )}
                  <div className="doc-head-center">
                    <div className="clinic-name">{printData.clinic?.clinic_name || 'Clínica'}</div>
                    {printData.clinic?.clinic_subtitle && <div className="clinic-subtitle">{printData.clinic.clinic_subtitle}</div>}
                  </div>
                  <div className="doc-head-meta">
                    {printData.clinic?.cnpj && <div>CNPJ: {printData.clinic.cnpj}</div>}
                    {printData.clinic?.address_street && (
                      <div>{printData.clinic.address_street}{printData.clinic.address_number ? `, ${printData.clinic.address_number}` : ''}{printData.clinic.address_neighborhood ? ` — ${printData.clinic.address_neighborhood}` : ''}</div>
                    )}
                    {(printData.clinic?.address_city || printData.clinic?.address_state) && (
                      <div>{[printData.clinic.address_city, printData.clinic.address_state].filter(Boolean).join(' - ')}</div>
                    )}
                    {(printData.clinic?.phone || printData.clinic?.whatsapp) && (
                      <div>Tel: {printData.clinic.phone || printData.clinic.whatsapp}</div>
                    )}
                    {printData.clinic?.email && <div>{printData.clinic.email}</div>}
                  </div>
                </div>

                {/* Prescription type & title */}
                <div className="prescription-title">
                  {printData.prescription?.prescription_type === 'controlled_blue' ? 'RECEITUÁRIO DE CONTROLE ESPECIAL' :
                   printData.prescription?.prescription_type === 'controlled_yellow' ? 'NOTIFICAÇÃO DE RECEITA A' :
                   printData.prescription?.prescription_type === 'special_white' ? 'NOTIFICAÇÃO DE RECEITA ESPECIAL' :
                   printData.prescription?.prescription_type === 'antimicrobial' ? 'RECEITUÁRIO - ANTIMICROBIANO' :
                   'RECEITUÁRIO'}
                </div>
                <div className="prescription-type-badge">
                  <span style={{ backgroundColor: `${typeColors[printData.prescription?.prescription_type] || '#14b8a6'}20`, color: typeColors[printData.prescription?.prescription_type] || '#14b8a6' }}>
                    {typeLabels[printData.prescription?.prescription_type] || 'Simples'}
                  </span>
                </div>
                <div className="prescription-number">
                  Nº {printData.prescription?.prescription_number} | Emissão: {formatDate(printData.prescription?.issue_date)} | Validade: {formatDate(printData.prescription?.validity_date)}
                  {printData.prescription?.via_count > 1 && ` | ${printData.prescription.via_count}ª Via`}
                </div>

                {/* Patient info */}
                <div className="section">
                  <div className="section-title">Dados do Paciente</div>
                  <div className="info-grid">
                    <div className="info-item"><span className="info-label">Nome: </span><span className="info-value">{printData.prescription?.patient_name}</span></div>
                    {printData.prescription?.patient_cpf && <div className="info-item"><span className="info-label">CPF: </span><span className="info-value">{printData.prescription.patient_cpf}</span></div>}
                    {printData.prescription?.patient_age_text && <div className="info-item"><span className="info-label">Idade: </span><span className="info-value">{printData.prescription.patient_age_text}</span></div>}
                    {printData.prescription?.patient_address && <div className="info-item"><span className="info-label">Endereço: </span><span className="info-value">{printData.prescription.patient_address}</span></div>}
                  </div>
                </div>

                {/* Diagnosis */}
                {(printData.prescription?.diagnosis || printData.prescription?.clinical_indication) && (
                  <div className="section">
                    <div className="section-title">Informações Clínicas</div>
                    {printData.prescription?.diagnosis && <div className="info-item"><span className="info-label">Diagnóstico: </span><span className="info-value">{printData.prescription.diagnosis}{printData.prescription?.diagnosis_code ? ` (CID: ${printData.prescription.diagnosis_code})` : ''}</span></div>}
                    {printData.prescription?.clinical_indication && <div className="info-item"><span className="info-label">Indicação: </span><span className="info-value">{printData.prescription.clinical_indication}</span></div>}
                  </div>
                )}

                {/* Medications */}
                <div className="section">
                  <div className="section-title">Prescrição</div>
                  {(printData.prescription?.items || []).map((item: any, idx: number) => (
                    <div className="medication-item" key={item.id || idx}>
                      <div className="medication-header">
                        <div className="medication-name">
                          {idx + 1}) {item.medication_name}
                          {item.concentration && ` ${item.concentration}`}
                          {item.pharmaceutical_form && ` - ${item.pharmaceutical_form}`}
                          {item.is_controlled && <span className="controlled-badge">CONTROLADO</span>}
                        </div>
                        {item.quantity && <div className="medication-number">{item.quantity}</div>}
                      </div>
                      <div className="medication-details">
                        <strong>Posologia:</strong> {item.dosage}, {item.frequency}
                        {item.route && <span> (via {item.route})</span>}
                        {item.duration && <span> por {item.duration}</span>}
                      </div>
                      {item.instructions && (
                        <div className="medication-instructions">
                          <strong>Instruções:</strong> {item.instructions}
                        </div>
                      )}
                      {item.special_instructions && (
                        <div className="medication-instructions">
                          <strong>Atenção:</strong> {item.special_instructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Notes */}
                {printData.prescription?.notes && (
                  <div className="section">
                    <div className="section-title">Observações</div>
                    <p style={{ fontSize: '11px', lineHeight: '1.6' }}>{printData.prescription.notes}</p>
                  </div>
                )}

                {/* Continuous use notice */}
                {printData.prescription?.is_continuous_use && (
                  <div style={{ textAlign: 'center', margin: '12px 0', padding: '6px', backgroundColor: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: '4px', fontSize: '11px', fontWeight: 600, color: '#0d9488' }}>
                    USO CONTÍNUO - Este receituário tem validade até renovação pelo profissional
                  </div>
                )}

                {/* Validity */}
                <div className="validity">
                  Receituário válido até {formatDate(printData.prescription?.validity_date)}
                </div>

                {/* Signatures */}
                <div className="clinic-doc-sign-zone">
                  <div className="clinic-doc-sign-row">
                    <div className="clinic-doc-sign-slot">
                      <div className="clinic-doc-sign-spacer" />
                      <div className="clinic-doc-sign-rule" />
                      <p className="clinic-doc-sign-caption">{printData.prescription?.professional_name || printData.clinic?.responsible_name || 'Profissional Responsável'}</p>
                      {(printData.prescription?.professional_registration || printData.clinic?.responsible_registration) && (
                        <p className="clinic-doc-sign-caption">
                          {printData.prescription?.professional_registration_type || printData.clinic?.responsible_registration_type || 'CRM'}{' '}
                          {printData.prescription?.professional_registration || printData.clinic?.responsible_registration}
                          {printData.prescription?.professional_registration_state ? ` - ${printData.prescription.professional_registration_state}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
                      <div className="clinic-doc-sign-spacer" />
                      <div className="clinic-doc-sign-rule" />
                      <p className="clinic-doc-sign-caption">Data: ___/___/______</p>
                    </div>
                  </div>
                </div>

                {/* Via info for controlled */}
                {printData.prescription?.via_count > 1 && (
                  <div className="via-info">
                    Este receituário deve ser emitido em {printData.prescription.via_count} vias
                  </div>
                )}

                {/* Footer */}
                {printData.clinic?.prescription_footer_text && (
                  <div className="footer-note">{printData.clinic.prescription_footer_text}</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ====== AI PRESCRIPTION DIALOG ====== */}
      <Dialog open={aiDialogOpen} onClose={() => !aiGenerating && setAiDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#8b5cf6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Receituário com IA</Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} disabled={aiGenerating}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              A IA irá gerar uma sugestão de receituário com base no medicamento informado. Revise sempre antes de salvar.
            </Alert>
            {aiError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{aiError}</Alert>}
          </Box>
          <TextField
            fullWidth
            label="Nome do Paciente *"
            placeholder="Ex: Maria Silva"
            value={aiPatientName}
            onChange={(e) => setAiPatientName(e.target.value)}
            disabled={aiGenerating}
            sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <TextField
            fullWidth
            label="Nome do Medicamento *"
            placeholder="Ex: Amoxicilina 500mg"
            value={aiMedicationName}
            onChange={(e) => setAiMedicationName(e.target.value)}
            disabled={aiGenerating}
            onKeyPress={(e) => { if (e.key === 'Enter' && !aiGenerating) handleAiGenerate(); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PharmacyIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAiGenerate}
            disabled={aiGenerating || !aiPatientName.trim() || !aiMedicationName.trim()}
            startIcon={aiGenerating ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600, minWidth: 180 }}
          >
            {aiGenerating ? 'Gerando receita...' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====== TEMPLATES MANAGER ====== */}
      <Dialog open={templateManagerOpen} onClose={() => setTemplateManagerOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon sx={{ color: '#0284c7' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Receituários Pré-preenchidos</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openTemplateCreate}
              sx={{ bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' }, textTransform: 'none' }}>
              Novo pré-preenchido
            </Button>
            <IconButton onClick={() => setTemplateManagerOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {templates.length === 0 ? (
            <Alert severity="info">Nenhum template cadastrado ainda.</Alert>
          ) : (
            <Grid container spacing={1.5}>
              {templates.map((tpl: any) => (
                <Grid item xs={12} md={6} key={tpl.id}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{tpl.name}</Typography>
                          {tpl.description && <Typography variant="caption" sx={{ color: '#64748b' }}>{tpl.description}</Typography>}
                        </Box>
                        <Chip size="small" label={typeLabels[tpl.prescription_type] || tpl.prescription_type} />
                      </Box>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {tpl.category && <Chip size="small" label={tpl.category} sx={{ bgcolor: '#f1f5f9' }} />}
                        {tpl.specialty && <Chip size="small" label={tpl.specialty} sx={{ bgcolor: '#f1f5f9' }} />}
                        <Chip size="small" label={`${tpl.items?.length || 0} medicamento(s)`} sx={{ bgcolor: '#f1f5f9' }} />
                      </Box>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="contained" onClick={() => { applyTemplate(tpl); setTemplateManagerOpen(false); setDialogOpen(true); }}
                          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>
                          Usar
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => openTemplateEdit(tpl)} sx={{ textTransform: 'none' }}>
                          Editar
                        </Button>
                        <Button size="small" color="error" onClick={() => deleteTemplate(tpl.id)} sx={{ textTransform: 'none' }}>
                          Excluir
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* ====== TEMPLATE CREATE/EDIT ====== */}
      <Dialog open={templateDialogOpen} onClose={() => !templateSaving && setTemplateDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{templateEditingId ? 'Editar Pré-preenchido' : 'Novo Pré-preenchido'}</Typography>
          <IconButton onClick={() => !templateSaving && setTemplateDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Nome do pré-preenchido *" value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Categoria" value={templateForm.category}
                onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Especialidade" value={templateForm.specialty}
                onChange={(e) => setTemplateForm({ ...templateForm, specialty: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descrição" value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f766e' }}>Medicamentos do pré-preenchido</Typography>
            <Button size="small" startIcon={<AddItemIcon />} onClick={() => setTemplateItems([...templateItems, { ...emptyItem, sort_order: templateItems.length }])}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Adicionar
            </Button>
          </Box>
          {templateItems.map((item: any, index: number) => {
            const updateTplItem = (field: string, value: any) => {
              const next = [...templateItems];
              next[index] = { ...next[index], [field]: value };
              setTemplateItems(next);
            };
            return (
            <Card key={index} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e5e7eb', position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Medicamento {index + 1}</Typography>
                {templateItems.length > 1 && (
                  <IconButton size="small" onClick={() => setTemplateItems(templateItems.filter((_: any, i: number) => i !== index))} sx={{ color: '#ef4444' }}>
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Nome do Medicamento *" value={item.medication_name}
                    onChange={(e) => updateTplItem('medication_name', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Princípio Ativo" value={item.active_ingredient || ''}
                    onChange={(e) => updateTplItem('active_ingredient', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Concentração" value={item.concentration || ''}
                    onChange={(e) => updateTplItem('concentration', e.target.value)} placeholder="Ex: 500mg" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Forma Farmacêutica</InputLabel>
                    <Select value={item.pharmaceutical_form || ''} label="Forma Farmacêutica"
                      onChange={(e) => updateTplItem('pharmaceutical_form', e.target.value)}>
                      {formOptions.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Posologia (Dosagem) *" value={item.dosage || ''}
                    onChange={(e) => updateTplItem('dosage', e.target.value)} placeholder="Ex: 1 comprimido" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth size="small" label="Frequência *" value={item.frequency || ''}
                    onChange={(e) => updateTplItem('frequency', e.target.value)} placeholder="Ex: 8 em 8 horas" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Via de Administração</InputLabel>
                    <Select value={item.route || 'Oral'} label="Via de Administração"
                      onChange={(e) => updateTplItem('route', e.target.value)}>
                      {routeOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Duração" value={item.duration || ''}
                    onChange={(e) => updateTplItem('duration', e.target.value)} placeholder="Ex: 7 dias" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth size="small" label="Quantidade" value={item.quantity || ''}
                    onChange={(e) => updateTplItem('quantity', e.target.value)} placeholder="Ex: 1 caixa" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel control={<Switch size="small" checked={!!item.is_controlled} onChange={(e) => updateTplItem('is_controlled', e.target.checked)} color="error" />}
                      label={<Typography variant="caption">Controlado</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={!!item.is_antimicrobial} onChange={(e) => updateTplItem('is_antimicrobial', e.target.checked)} color="warning" />}
                      label={<Typography variant="caption">Antimicrobiano</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={!!item.is_continuous_use} onChange={(e) => updateTplItem('is_continuous_use', e.target.checked)} color="info" />}
                      label={<Typography variant="caption">Uso Contínuo</Typography>} />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Instruções de Uso" value={item.instructions || ''}
                    onChange={(e) => updateTplItem('instructions', e.target.value)}
                    placeholder="Ex: Tomar após as refeições, não ingerir com leite" multiline rows={2} />
                </Grid>
              </Grid>
            </Card>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={() => setTemplateDialogOpen(false)} disabled={templateSaving}>Cancelar</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={templateSaving}
            startIcon={templateSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' }, textTransform: 'none', fontWeight: 600 }}>
            {templateSaving ? 'Salvando...' : 'Salvar Pré-preenchido'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="prescription"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientName={sendDocData?.name}
        documentLabel="Receituário"
      />
    </Box>
  );
};

export default Prescriptions;
